import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { PosSale, CompanyPosSales, PosSalePaymentParameter } from '../../models/pos.sale.model';
import { Customer } from '../../models/customer.model';
import { Currency } from '../../models/currency.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { authToken } from '../../scripts/authentication.script';
import { Error } from '../../models/error.model';

export class PosSaleDA {
  static apiResponse: ApiResponse

  public static async getSales(
    id_company: string,
    id_pos_cashier: string,
    interval: string,
    start_date: string,
    end_date: string,
    sort_field: string,
    sort_mode: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyPosSales: CompanyPosSales | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/pos.sales.data.json').then(response => { companyPosSales = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyPosSales!.id_company === id_company && companyPosSales!.id_pos_cashier === id_pos_cashier) {
        companyPosSales!.filter = { interval: interval, start_date: start_date, end_date: end_date, sort_field: sort_field, sort_mode: sort_mode }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyPosSales
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/${id_pos_cashier}/sales?interval=${interval}&start_date=${start_date}&end_date=${end_date}&sort_field=${sort_field}&sort_mode=${sort_mode}&page=${page}&limit=${limit}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getSale(
    id_company: string,
    id_pos_sale: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let posSale: PosSale | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/pos.sale.data.json').then(response => { posSale = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (posSale!.id === id_pos_sale) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: posSale
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/sales/${id_pos_sale}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }
    return this.apiResponse
  }

  public static async postSale(
    id_company: string,
    id_pos: string,
    customer: Customer | null,
    currency: Currency | null,
    requirements: { reference: string, type: string, description: string, unit: string, quantity: string, price_unit: string, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, product_account_id?: string | null }[],
    payments: PosSalePaymentParameter[],
    expected_amount?: { sub_total: number, rebate_total: number, tax_total: number, total: number },
    company?: { uuid: string, name: string, logo: string | null, address: any },
    company_tax?: { subject_tax: boolean, tax_method: string, tax_declaration: string },
    language?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/sales`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        id_pos: id_pos,
        requirements: requirements.map(r => ({
          reference: r.reference,
          type: r.type,
          description: r.description,
          unit: r.unit,
          quantity: Number(r.quantity),
          price_unit: Number(r.price_unit),
          tax_code: r.tax_code,
          tax_rate: Number(r.tax_rate),
          rebate_type: r.rebate_type,
          rebate_value: Number(r.rebate_value),
          rebate_quantity: Number(r.rebate_quantity),
          rebate_amount: Number(r.rebate_amount),
          product_account_id: r.product_account_id || null,
        })),
        payments: payments.map(p => ({
          method: p.method,
          currency_code: p.currency_code || 'CHF',
          amount: Number(p.amount),
          exchange_rate: Number(p.exchange_rate) || 1,
          exchange_date: (p.currency_code && p.currency_code !== 'CHF') ? (p.exchange_date || null) : null,
          id_treasury_account: p.id_treasury_account,
          received: Number(p.received) || Number(p.amount),
          change_amount: Number(p.change_amount) || 0,
          change_currency_code: p.change_currency_code || 'CHF',
          id_accounting_entry: p.id_accounting_entry || null,
        })),
      }

      if (expected_amount) {
        data.expected_amount = {
          sub_total: Number(expected_amount.sub_total),
          rebate_total: Number(expected_amount.rebate_total),
          tax_total: Number(expected_amount.tax_total),
          total: Number(expected_amount.total),
        }
      }

      if (customer) {
        data.customer = customer
      }

      if (currency) {
        data.currency = currency
      }

      if (company) {
        data.company = company
      }

      if (language) {
        data.language = language
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
